import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, getGraphColor, mediaQueriesNumbers, useMediaQuery } from "../../../Styles";
import { NewSelect, NewInput, NewModal, MainContentPaddingTypes } from "../..";
import { ListData_ValueTypes, TableStyles_Types, ButtonTypes, NewFieldStyles, NewInputHandles, ValueSteps } from "../../../Definitions";
import { Graph_QL, useFeedCurves, FeedCurve, MaintainFeedCurveUpdateComponent } from "../../../Services";
import { useAppSelector } from "../../../Store";
import { keyBy, merge, noop } from "lodash";

// Table
import { ReactiveTable } from "../../Tables/reactiveTableRenderer";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useColumnConstructor } from "../../Tables/_Tables/useColumnConstructor";
import NewButton from "../../BaseComponents/newButton";

const getComponentTableDivStyle = (id?: string | number):CSSProperties|undefined => {
  if (!id) { return }
  return {backgroundColor: getGraphColor(+id), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary}
}
interface props {
  closeModal: () => void,
  feedCurve: FeedCurve,
}

export default function FeedCurveSettingsModal({closeModal, feedCurve}: props) {
  const {t} = useTranslation()
  const dirtRef = useRef<MaintainFeedCurveUpdateComponent[]>([]);
  const feedLimitRef = useRef<NewInputHandles>(null);
  const passFeedRef = useRef<NewInputHandles>(null);
  const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
  
  const feedCurveComponents = useMemo(() => {
    return feedCurve.components?.map((component) => ({...component, id: component.feedCurveComponentId})) ?? []
  },[feedCurve.components])

  // Connection
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const connection = useMemo(() => (Graph_QL.create({domain: farm ?? ""})),[farm]);
 
  // #region | Feed Curves data
  const {maintainEntries: maintainCurveSettings } = useFeedCurves(connection);
  
  // #endregion

  // #region | Remaining Feed
  const [remainingFeed, setRemainingFeed] = useState<number>(feedCurve.passFeed > 0  ? 1 : 0)

  const remainingFeedOptions = useMemo(() => ([    
    { label: t("feedCurvePages.feedCurve.doNotPassFeed", {defaultValue: "Do not pass feed"}), id: 0 },
    { label: t("feedCurvePages.feedCurve.passFeed", {defaultValue: "Pass feed"}), id: 1 },
  ]),[t])
  // #endregion

  // #region | Stimuli
  const [stimuli, setStimuli] = useState<number>(feedCurve.stimuli > 0 ? 1 : 0)
  const [stimuliRepeat, setStimuliRepeat] = useState<number>(0)
  
  // useEffect(() => {
  //   if (feedCurve.stimuli > 0) {
  //     setStimuli(1);
  //   }
  //   if (feedCurve.passFeed > 0) {
  //     setRemainingFeed(1);
  //   }
  // },[feedCurve.passFeed, feedCurve.stimuli])
  
  useEffect(() => {
    setStimuli(feedCurve.stimuli > 0 ? 1 : 0);
    setRemainingFeed(feedCurve.passFeed > 0 ? 1 : 0);
  }, [feedCurve]);

  const stimuliOptions = useMemo(() => ([    
    { label: t("feedCurvePages.feedCurve.noStimuli", {defaultValue: "No stimuli"}), id: 0 },
    { label: t("feedCurvePages.feedCurve.addStimuli", {defaultValue: "Add stimuli"}), id: 1 },
  ]),[t])

  const stimuliRepeatOptions = useMemo(() => ([
    { label: t("feedCurvePages.feedCurve.singleStimuli", {defaultValue: "Single stimuli"}), id: 0 },
    // { label: t("feedCurvePages.feedCurve.repeatStimuli", {defaultValue: "Repeat stimuli"}), id: 1 }, 
  ]),[t])
  // #endregion

  // #region | Components Table
  const accessorData = useMemo(() => ([
    {headerValue: "", accessor: "name", type: ListData_ValueTypes.Div, staticColumn: true, divStyleOverride: getComponentTableDivStyle, styleOverrideOnValue: false},
    {headerValue: "", accessor: "gramPerMinute", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers}
  ]),[])
  
  const columns = useColumnConstructor({accessorData, tableStyle: TableStyles_Types.noHeaderTable, canEdit: true });

  const table = useReactTable({
    columns,
    data: feedCurveComponents,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      updateData: (feedCurveComponentId, columnId, value) => {
        dirtRef.current = (Object.values(merge(keyBy(dirtRef.current, "feedCurveComponentId"), keyBy([{feedCurveComponentId, baseInfo: {"gram_per_minute": value ? +value: undefined}}], "feedCurveComponentId"))))
      },
      deleteRow: noop
    }
  })
  // #endregion

  return (
    <NewModal
      header={t("feedCurvePages.feedCurve.settings", {defaultValue: "settings"})}
      subHeader={`${t("feed.feedCurve.feedCurve", {defaultValue: "feed Curve"})} ${feedCurve.feedCurveId}: ${feedCurve.name}`}
      predefinedButtons={{
        onClose: () => {closeModal(); dirtRef.current=[]}
      }}
      overrides={{
        mainContentPadding: MainContentPaddingTypes.secondary
      }}
      secondaryContent={
        // <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "40px", marginTop: "10px"}}>
          
        <div style={{ width: mobileXs ? "290px" : "600px", display: "flex", gap: "20px", flexDirection: "column"}}>
            {/* Components / Feed Speed */}
            <div style={{width: "100%"}}>
              <div style={{display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gridTemplateRows: "1fr"}}>
                <p style={{ gridArea: "1/1/2/2", ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 16px 2px 16px" }}>{t("feedCurvePages.components.components", {defaultValue: "components"})}</p>
                <p style={{ gridArea: "1/2/2/3", ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 16px 2px 16px" }}>{`${t("feed.feedCurve.feedSpeed", {defaultValue: "feed speed"})} (${t("g", {defaultValue: "g"})}/${t("min", {defaultValue: "min"})})`}</p>
              </div>
              {ReactiveTable({table, tableType: TableStyles_Types.noHeaderTable, emptyText: t("feedCurvePages.feedCurve.noComponents", {defaultValue: "no components found"}), renderFooter: false})}
            </div>

            {/* Max Periode Amount */}
            {/* <div style={{width: "100%", display: "flex", flexDirection: "column"}}> */}
              <NewInput
                ref={feedLimitRef}
                header={t("feedCurvePages.feedCurve.maxAmount", {defaultValue: "max period amount (kg)"})}
                fieldOverrides={{
                  fieldStyle: NewFieldStyles.blueDarkSecondary,
                  fieldWidth: mobileXs ? "100%" : undefined
                }}
                valueOverrides={{
                  defaultValue: feedCurve.feedLimit/1000,
                  valueType: "number",
                  minValueNumber: 0,
                  valueStep: ValueSteps.floats
                }}
                placeholder={t("units.kg", {defaultValue: "KG"})}
              />
            {/* </div> */}

            {/* Remaining Feed */}
            <div style={{width: "100%", display: "flex", flexDirection: "column"}}>
              <NewSelect
                header={t("feedCurvePages.feedCurve.remainingFeed", {defaultValue: "remaining Feed"})} 
                options={remainingFeedOptions}
                onChange={(selectedOption) => selectedOption && setRemainingFeed(selectedOption.id as number)}
                valueOverrides={{
                  defaultValue: remainingFeedOptions.find(option => option.id === remainingFeed),
                }}
                fieldOverrides={{
                  fieldWidth: mobileXs ? "100%" : undefined,
                  fieldStyle: NewFieldStyles.blueDarkSecondary
                }}
              />
              { remainingFeed === 1 && (
                <div style={{ boxSizing: "border-box", height: "fit-content", gap: "5px", alignItems: "center", backgroundColor: colors.greyLightSecondary, padding: "10px 10px", borderRadius: "10px", marginTop: "10px", display: "flex", flexDirection: "row" }}>
                  <NewInput
                    ref={passFeedRef}
                    placeholder={t("units.kg", {defaultValue: "KG"})}
                    fieldOverrides={{
                      fieldStyle: NewFieldStyles.greyTertiary,
                      fieldWidth: mobileXs ? "100%" : undefined
                    }}
                    valueOverrides={{
                      valueType: "number",
                      defaultValue: (feedCurve.passFeed) / 1000,
                      valueStep: ValueSteps.floats,
                      minValueNumber: 0
                    }}
                  />
                  <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, whiteSpace: "nowrap"}}>{t("feedCurvePages.feedCurve.maxFeedToPass", {defaultValue: "max kg to pass"})}</p>
                </div> 
              )}
            </div>

            {/* Feed Curve Stimuli */}
            <div style={{width: "100%", display: "flex", flexDirection: "column"}}>
              <NewSelect 
                header={t("feedCurvePages.feedCurve.feedStimuli", {defaultValue: "feed stimuli"})}
                options={stimuliOptions}
                onChange={(selectedOption) => selectedOption && setStimuli(selectedOption.id as number)}
                valueOverrides={{
                  defaultValue: stimuliOptions.find(option => option.id === stimuli)
                }}
                fieldOverrides={{
                  fieldStyle: NewFieldStyles.blueDarkSecondary,
                  fieldWidth: mobileXs ? "100%" : undefined
                }}
              />
              { stimuli === 1 && (
                <div style={{ width: "calc(100% - 20px)", height: "fit-content", backgroundColor: colors.greyLightSecondary, padding: "10px 10px", borderRadius: "10px", marginTop: "10px", display: "flex", flexDirection: "row", gap: "10px" }}>
                  <NewSelect 
                    options={stimuliRepeatOptions}
                    onChange={(selectedOption) => selectedOption && setStimuliRepeat(selectedOption.id as number)}    
                    valueOverrides={{
                      defaultValue: stimuliRepeatOptions.find(option => option.id === stimuliRepeat)
                    }}
                    fieldOverrides={{
                      fieldStyle: NewFieldStyles.greyTertiary,
                      fieldWidth: mobileXs ? "100%" : "150px"
                    }}
                  />
                  { stimuliRepeat === 1 && (
                    <div style={{display: "flex", flexDirection: "row", gap: "5px", alignItems: "center"}}>
                      <NewInput 
                        fieldOverrides={{
                          fieldStyle: NewFieldStyles.greyTertiary,
                          fieldWidth: mobileXs ? "100%" : "150px"
                        }}
                        valueOverrides={{
                          valueType: "number"
                        }}
                      />
                      <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{t("feedCurvePages.feedCurve.minutesApart", {defaultValue: "minutes apart"})}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
      }
      footerContent={
        <NewButton content={t("buttons.save", {defaultValue: "save"})} buttonType={ButtonTypes.primaryButton} 
        overrides={{
          baseStyles: {justifyContent: "center"},
          hoverStyles: {backgroundColor: colors.blackPrimary}
        }}
        onClick={() => {
          if (maintainCurveSettings) {maintainCurveSettings({data: {components: dirtRef.current, baseInfo: {feed_limit: feedLimitRef.current?.value ? Math.round(+feedLimitRef.current.value*1000): undefined, pass_feed: passFeedRef.current?.value ? Math.round(+passFeedRef.current.value*1000) : 0, stimuli}}}, undefined, undefined, {originalFeedCurve: feedCurve, optimisticFeedCurve: feedCurve});}
          dirtRef.current = []
          closeModal();
        }}
      />
      }
    />
  );
}



          // let pass_feed = undefined;
          // if (passFeedRef.current?.value) {
          //  pass_feed = Math.round(+passFeedRef.current.value*1000); 
          // }
          // if (remainingFeed === 0) {
          //   pass_feed = 0
          // }
          // const feed_limit = feedLimitRef.current?.value ? Math.round(+feedLimitRef.current.value*1000): undefined
          // if (maintainCurveSettings) {maintainCurveSettings({data: {components: dirtRef.current, baseInfo: {feed_limit, pass_feed, stimuli}}}, undefined, undefined, {originalFeedCurve: feedCurve, optimisticFeedCurve: feedCurve});}